import React, { useState, useEffect } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { useUserPlan } from '@/hooks/useUserPlan';
import { useRealTimeNotifications } from '@/hooks/useRealTimeNotifications';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, CreditCard, Bell, Shield, TrendingUp, Check, Store } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Pricing from '@/components/Pricing';
import { EtsyIntegration } from '@/components/EtsyIntegration';
interface NotificationSettings {
  email_notifications: boolean;
  generation_alerts: boolean;
  marketing_emails: boolean;
}
const Settings = () => {
  const {
    user,
    signOut,
    sendPasswordReset
  } = useSecureAuthContext();
  const navigate = useNavigate();
  const {
    userPlan,
    loading: planLoading,
    refetch: refetchPlan,
    getPlanDisplayName,
    getPlanBadgeColor
  } = useUserPlan();
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    clearAll
  } = useRealTimeNotifications();
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    email_notifications: true,
    generation_alerts: true,
    marketing_emails: false
  });
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [autoProcessImages, setAutoProcessImages] = useState(true);
  useEffect(() => {
    if (user) {
      loadNotificationSettings();
      checkAdminStatus();
      loadProcessingSettings();
    }
  }, [user]);
  useEffect(() => {
    if (userPlan) {
      setProfileName(userPlan.full_name || '');
    }
  }, [userPlan]);
  const checkAdminStatus = async () => {
    try {
      const {
        data,
        error
      } = await supabase.rpc('is_current_user_admin');
      if (error) {
        console.error('Error checking admin status:', error);
        return;
      }
      setIsAdmin(data || false);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };
  const loadNotificationSettings = () => {
    const saved = localStorage.getItem('notificationSettings');
    if (saved) {
      setNotificationSettings(JSON.parse(saved));
    }
  };
  const loadProcessingSettings = async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('auto_process_images')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      if (data?.auto_process_images !== undefined) {
        setAutoProcessImages(data.auto_process_images);
      }
    } catch (error) {
      console.error('Error loading processing settings:', error);
    }
  };
  const updateAutoProcessImages = async (enabled: boolean) => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          auto_process_images: enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      setAutoProcessImages(enabled);
      toast.success(
        enabled 
          ? 'Auto-processing enabled. Images will be enhanced, have backgrounds removed, and converted to multiple formats.'
          : 'Auto-processing disabled. Generated images will be stored as-is.'
      );
    } catch (error) {
      toast.error('Failed to update processing settings');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  const updateProfile = async () => {
    if (!user?.id || !profileName.trim()) {
      toast.error('Please enter a valid name');
      return;
    }
    setLoading(true);
    try {
      const {
        error
      } = await supabase.from('profiles').update({
        full_name: profileName.trim(),
        updated_at: new Date().toISOString()
      }).eq('id', user.id);
      if (error) throw error;
      toast.success('Profile updated successfully!');
      refetchPlan();
    } catch (error) {
      toast.error('Error updating profile');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  const updateNotifications = (key: keyof NotificationSettings, value: boolean) => {
    const updated = {
      ...notificationSettings,
      [key]: value
    };
    setNotificationSettings(updated);
    localStorage.setItem('notificationSettings', JSON.stringify(updated));
    toast.success('Notification settings updated');
  };
  const resetCredits = async () => {
    if (!isAdmin) {
      toast.error('Only administrators can reset credits');
      return;
    }
    try {
      const {
        error
      } = await supabase.from('profiles').update({
        credits_remaining: 100,
        image_generations_used: 0,
        updated_at: new Date().toISOString()
      }).eq('id', user?.id);
      if (error) throw error;
      toast.success('Credits reset successfully!');
      refetchPlan();
    } catch (error) {
      toast.error('Error resetting credits');
      console.error('Error:', error);
    }
  };
  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut();
      toast.success('Signed out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Error signing out');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleChangePassword = async () => {
    if (!user?.email) {
      toast.error('User email not found');
      return;
    }
    try {
      setLoading(true);
      console.log('=== SETTINGS: PASSWORD RESET REQUESTED ===');
      console.log('Email:', user.email);
      console.log('Using sendPasswordReset from SecureAuthContext...');
      const {
        error
      } = await sendPasswordReset(user.email);
      console.log('sendPasswordReset result:', {
        error
      });
      if (error) {
        console.error('Password reset error in Settings:', error);
        toast.error(error.message);
      } else {
        console.log('Password reset successful in Settings');
        toast.success('Password reset email sent! Check your inbox and click the link to reset your password.');
      }
    } catch (error) {
      console.error('Error sending password reset email:', error);
      toast.error('Error sending password reset email');
    } finally {
      setLoading(false);
    }
  };
  const handlePlanClick = () => {
    window.open("https://forms.gle/yWSKnznK4DGmTxqn9", "_blank");
  };
  const getPlanDescription = (plan: string) => {
    switch (plan) {
      case 'free':
        return '250 images per month, Standard resolution, Basic AI models';
      case 'hobby':
        return '5,000 images per month, HD quality, All standard AI models';
      case 'creator':
        return '16,000 images per month, HD + Premium options, All AI models + latest releases';
      case 'professional':
        return '50,000 images per month, All options including Ultra HD, All AI models';
      case 'business':
        return '150,000 images per month, All + custom resolutions, 24/7 dedicated support';
      case 'pay_as_go':
        return 'No monthly commitment, $0.04 per image, HD quality, All AI models';
      case 'enterprise':
        return 'Unlimited images, Custom AI models, Premium quality, 24/7 dedicated support';
      default:
        return '';
    }
  };
  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'welcome':
        return 'bg-purple-600';
      case 'generation':
        return 'bg-blue-600';
      case 'success':
        return 'bg-green-600';
      case 'processing':
        return 'bg-yellow-600';
      case 'feature':
        return 'bg-purple-600';
      case 'usage':
        return 'bg-orange-600';
      case 'error':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };
  const markNotificationAsRead = async (id: string) => {
    try {
      await markAsRead(id);
      toast.success('Notification marked as read');
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };
  const clearAllNotifications = async () => {
    try {
      await markAllAsRead(); // Use update instead of delete to satisfy RLS
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };
  if (planLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading settings...</div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      </div>

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-cyan-500/30 rounded-full blur-2xl animate-bounce"></div>

      <header className="bg-black/20 backdrop-blur-md border-b border-white/20 relative z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="flex items-center text-white hover:text-blue-400 transition-colors">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </Link>
            </div>
            
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">BG</span>
              </div>
              <span className="text-xl font-bold text-white">The Bulk Image Generator</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto px-6 py-12 relative z-10 w-full max-w-none">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
            <p className="text-gray-300">Manage your account and preferences</p>
          </div>

          <div className="space-y-8 w-full">
            {/* Horizontal Navigation */}
            <div className="w-full">
              <nav className="flex flex-wrap gap-2 justify-center lg:justify-start">
                <button onClick={() => setActiveSection('profile')} className={`flex items-center px-6 py-3 rounded-lg transition-colors ${activeSection === 'profile' ? 'text-blue-400 bg-blue-500/20' : 'text-gray-300 hover:bg-white/10'}`}>
                  <User className="w-5 h-5 mr-2" />
                  Profile
                </button>
                <button onClick={() => setActiveSection('billing')} className={`flex items-center px-6 py-3 rounded-lg transition-colors ${activeSection === 'billing' ? 'text-blue-400 bg-blue-500/20' : 'text-gray-300 hover:bg-white/10'}`}>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Billing & Plans
                </button>
                <button onClick={() => setActiveSection('notifications')} className={`flex items-center px-6 py-3 rounded-lg transition-colors ${activeSection === 'notifications' ? 'text-blue-400 bg-blue-500/20' : 'text-gray-300 hover:bg-white/10'}`}>
                  <Bell className="w-5 h-5 mr-2" />
                  Notifications
                </button>
                <button onClick={() => setActiveSection('security')} className={`flex items-center px-6 py-3 rounded-lg transition-colors ${activeSection === 'security' ? 'text-blue-400 bg-blue-500/20' : 'text-gray-300 hover:bg-white/10'}`}>
                  <Shield className="w-5 h-5 mr-2" />
                  Security
                </button>
                <button onClick={() => setActiveSection('integrations')} className={`flex items-center px-6 py-3 rounded-lg transition-colors ${activeSection === 'integrations' ? 'text-blue-400 bg-blue-500/20' : 'text-gray-300 hover:bg-white/10'}`}>
                  <Store className="w-5 h-5 mr-2" />
                  Integrations
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="w-full space-y-6">
              {/* Profile Settings */}
              {activeSection === 'profile' && userPlan && <Card className="bg-white/10 backdrop-blur-md border-white/20">
                  <CardHeader>
                    <CardTitle className="text-white">Profile Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="fullName" className="text-gray-300">Full Name</Label>
                      <Input id="fullName" value={profileName} onChange={e => setProfileName(e.target.value)} className="bg-white/10 border-white/20 text-white" placeholder="Enter your full name" />
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email</Label>
                      <Input id="email" value={userPlan.email} disabled className="bg-gray-800/50 border-white/20 text-gray-400" />
                    </div>
                    
                    <Button onClick={updateProfile} disabled={loading} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      {loading ? 'Updating...' : 'Update Profile'}
                    </Button>
                  </CardContent>
                </Card>}

              {/* Image Processing Settings */}
              {activeSection === 'profile' && <Card className="bg-white/10 backdrop-blur-md border-white/20">
                  <CardHeader>
                    <CardTitle className="text-white">Image Processing Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex-1">
                        <Label htmlFor="auto-process" className="text-gray-300 font-medium">
                          Auto-Process Generated Images
                        </Label>
                        <p className="text-gray-400 text-sm mt-1">
                          Automatically enhance quality to 600 DPI, remove backgrounds, and convert to multiple formats (PNG, JPG, PDF, SVG, WebP) after generation
                        </p>
                      </div>
                      <Switch 
                        id="auto-process" 
                        checked={autoProcessImages} 
                        onCheckedChange={updateAutoProcessImages}
                        disabled={loading}
                      />
                    </div>
                  </CardContent>
                </Card>}

              {/* Billing & Plans */}
              {activeSection === 'billing' && userPlan && <div className="space-y-6">
                  {/* Current Plan Card */}
                  <Card className="bg-white/10 backdrop-blur-md border-white/20 relative group hover:bg-white/[0.08] hover:border-white/30 transition-all duration-700">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-white flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Current Plan
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-xl font-bold capitalize text-white">{getPlanDisplayName(userPlan.plan_type)}</h3>
                            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white ${getPlanBadgeColor(userPlan.plan_type)}`}>
                              {getPlanDisplayName(userPlan.plan_type).toUpperCase()}
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm mb-4 leading-relaxed">{getPlanDescription(userPlan.plan_type)}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                              <p className="text-gray-400 text-xs mb-1">Credits Remaining</p>
                              <p className="text-white font-semibold text-lg">{userPlan.credits_remaining}</p>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                              <p className="text-gray-400 text-xs mb-1">Monthly Limit</p>
                              <p className="text-white font-semibold text-lg">{userPlan.monthly_limit === 999999 ? 'Unlimited' : userPlan.monthly_limit}</p>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                              <p className="text-gray-400 text-xs mb-1">Used This Month</p>
                              <p className="text-white font-semibold text-lg">{userPlan.image_generations_used}</p>
                            </div>
                          </div>
                        </div>
                        {isAdmin && <Button onClick={resetCredits} className="bg-transparent border-white/20 text-white hover:bg-white/10 ml-4">
                            Reset Credits
                          </Button>}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Available Plans - Using Home Page Pricing Component */}
                  <div className="w-full -mx-6">
                    <Pricing />
                  </div>
                </div>}

              {/* Notifications */}
              {activeSection === 'notifications' && <div className="space-y-6">
                  <Card className="bg-white/10 backdrop-blur-md border-white/20">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Bell className="w-5 h-5" />
                        Notification Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                        <div>
                          <Label htmlFor="email-notifications" className="text-gray-300 font-medium">Email Notifications</Label>
                          <p className="text-gray-400 text-sm mt-1">Receive general notifications via email</p>
                        </div>
                        <Switch id="email-notifications" checked={notificationSettings.email_notifications} onCheckedChange={checked => updateNotifications('email_notifications', checked)} />
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                        <div>
                          <Label htmlFor="generation-alerts" className="text-gray-300 font-medium">Generation Alerts</Label>
                          <p className="text-gray-400 text-sm mt-1">Get notified when image generation is complete</p>
                        </div>
                        <Switch id="generation-alerts" checked={notificationSettings.generation_alerts} onCheckedChange={checked => updateNotifications('generation_alerts', checked)} />
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                        <div>
                          <Label htmlFor="marketing-emails" className="text-gray-300 font-medium">Marketing Emails</Label>
                          <p className="text-gray-400 text-sm mt-1">Receive updates about new features and promotions</p>
                        </div>
                        <Switch id="marketing-emails" checked={notificationSettings.marketing_emails} onCheckedChange={checked => updateNotifications('marketing_emails', checked)} />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Real-time Notifications - Fixed height container */}
                  <Card className="bg-white/10 backdrop-blur-md border-white/20">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-white">Recent Notifications</CardTitle>
                        <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                          <span className="text-green-400 text-xs font-medium">Real-time Active</span>
                        </div>
                      </div>
                      {notifications.length > 0 && <Button onClick={clearAllNotifications} variant="outline" size="sm" className="text-gray-300 border-white/20 bg-white/0">
                          Clear All
                        </Button>}
                    </CardHeader>
                    <CardContent>
                      {notifications.length === 0 ? <div className="text-center py-12">
                          <Bell className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                          <p className="text-gray-400 text-lg">No notifications yet</p>
                          <p className="text-gray-500 text-sm mt-2">We'll notify you when there's something important</p>
                          <Button 
                            onClick={async () => {
                              try {
                                const response = await supabase.functions.invoke('test-notification', {
                                  body: {
                                    userId: user?.id,
                                    title: 'Test Real-time Notification',
                                    message: 'This notification was created to test real-time functionality! 🔔',
                                    type: 'info'
                                  }
                                });
                                if (response.error) {
                                  toast.error('Failed to create test notification');
                                } else {
                                  toast.success('Test notification created! Watch for real-time update.');
                                }
                              } catch (error) {
                                toast.error('Error creating test notification');
                              }
                            }}
                            className="mt-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                          >
                            🧪 Test Real-time Notifications
                          </Button>
                        </div> : <div className="space-y-3 h-80 overflow-y-auto pr-2 custom-scrollbar">
                          {notifications.map(notification => <div key={notification.id} className={`p-4 rounded-lg border transition-all duration-300 hover:scale-[1.02] ${notification.read ? 'bg-white/5 border-white/10 hover:bg-white/8' : 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/15 shadow-lg shadow-blue-500/10'}`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className={`w-3 h-3 rounded-full ${getNotificationTypeColor(notification.type)} animate-pulse`}></div>
                                    <span className="text-white font-semibold capitalize">{notification.type}</span>
                                    <span className="text-gray-400 text-xs bg-white/5 px-2 py-1 rounded-full">
                                      {new Date(notification.timestamp).toLocaleString()}
                                    </span>
                                  </div>
                                  <h4 className="text-white font-medium mb-2 text-lg">{notification.title}</h4>
                                  <p className="text-gray-300 text-sm leading-relaxed">{notification.message}</p>
                                </div>
                                {!notification.read && <Button onClick={() => markNotificationAsRead(notification.id)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 transition-all duration-200">
                                    Mark as read
                                  </Button>}
                              </div>
                            </div>)}
                        </div>}
                    </CardContent>
                  </Card>
                </div>}

              {/* Security */}
              {activeSection === 'security' && userPlan && <Card className="bg-white/10 backdrop-blur-md border-white/20">
                  <CardHeader>
                    <CardTitle className="text-white">Security</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-white">Account Email</Label>
                        <p className="text-sm text-gray-300">{userPlan.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-white">Password Reset</Label>
                        <p className="text-sm text-gray-300">Request a password reset link via email</p>
                      </div>
                      <Button onClick={handleChangePassword} variant="ghost" className="bg-transparent border-white/20 text-white hover:bg-white/10" disabled={loading}>
                        {loading ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-white">Sign Out</Label>
                        <p className="text-sm text-gray-300">Sign out of your account</p>
                      </div>
                      <Button onClick={handleSignOut} variant="ghost" className="bg-transparent border-white/20 text-white hover:bg-white/10" disabled={loading}>
                        {loading ? 'Signing Out...' : 'Sign Out'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>}

              {/* Integrations */}
              {activeSection === 'integrations' && (
                <div className="space-y-6">
                  <EtsyIntegration />
                  
                  <Card className="bg-white/10 backdrop-blur-md border-white/20">
                    <CardHeader>
                      <CardTitle className="text-white">More Integrations Coming Soon</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-300">
                        We're working on adding more integrations like Shopify, Amazon, and more. 
                        Stay tuned for updates!
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>;
};
export default Settings;